import { create } from 'zustand';

/** Direction the speech-bubble pointer arrow faces (toward the feature). */
export type PointerDirection = 'up' | 'down' | 'left' | 'right' | 'none';

export type TourStepAction =
  | 'expand-controls'
  | 'collapse-controls'
  | 'expand-left-menu'
  | 'collapse-left-menu'
  | 'show-bottom-sheet'
  | 'show-bottom-sheet-half'
  | 'hide-bottom-sheet'
  | 'expand-campaign-dock'
  | 'collapse-campaign-dock'
  | 'none';

export interface TourStep {
  id: string;
  title: string;
  description: string;
  /** Action to perform BEFORE showing this step */
  action?: TourStepAction;
  /** Skip automatically when `[data-tour-target]` is absent (e.g. field campaigns for non-field users). */
  skipIfMissingTarget?: boolean;
  /** Position of the card on screen */
  position: 'center' | 'bottom' | 'top-left' | 'top-right' | 'bottom-right' | 'bottom-left';
  /** Direction the pointer arrow faces (toward the highlighted feature) */
  pointer: PointerDirection;
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'welcome',
    title: 'Welcome to Coordal Maps',
    description:
      'Ghana\'s geospatial infrastructure — verified addresses, postcodes, maps, and location data.',
    action: 'hide-bottom-sheet',
    position: 'center',
    pointer: 'none',
  },
  {
    id: 'bottom-sheet',
    title: 'Your Places & Explore',
    description:
      'Swipe up to see your saved places, set home and work locations, and explore nearby food, hospitals, fuel, and more.',
    action: 'show-bottom-sheet',
    position: 'center',
    pointer: 'down',
  },
  {
    id: 'search',
    title: 'Search for places',
    description:
      'Find any address, building, road, or place of interest across Ghana. Try typing a place name or postcode.',
    action: 'hide-bottom-sheet',
    position: 'top-left',
    pointer: 'up',
  },
  {
    id: 'controls-toggle',
    title: 'Map controls',
    description: 'Tap this button to reveal the map controls — zoom, tilt, 3D view, and more.',
    action: 'expand-controls',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: '3d-toggle',
    title: '2D / 3D buildings',
    description: 'Toggle between flat 2D and realistic 3D building views on the map.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'zoom',
    title: 'Zoom in & out',
    description: 'Use these buttons to zoom the map closer or further away.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'tilt',
    title: 'Tilt the map',
    description: 'Tilt for a different perspective. Great for seeing buildings and terrain in 3D.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'locate',
    title: 'Find your location',
    description: 'Tap here to fly to your current location on the map.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'add-location',
    title: 'Add a missing place',
    description:
      'Tap the green Contribute button in the left menu to submit an address or place that is not on the map yet.',
    action: 'expand-left-menu',
    position: 'bottom-left',
    pointer: 'left',
  },
  {
    id: 'profile',
    title: 'Your profile',
    description: 'Sign in or view your profile, saved places, and account settings.',
    action: 'none',
    position: 'bottom-left',
    pointer: 'left',
  },
  {
    id: 'field-campaigns',
    title: 'Field campaigns',
    description:
      'When you are on a campaign team, a teal map pin appears in this menu. Tap it to open your assigned field campaigns.',
    action: 'expand-left-menu',
    position: 'bottom-left',
    pointer: 'left',
    skipIfMissingTarget: true,
  },
  {
    id: 'appearance',
    title: 'Appearance',
    description: 'Switch between light and dark mode, or change the map style.',
    action: 'none',
    position: 'bottom-left',
    pointer: 'left',
  },
  {
    id: 'weather-forecast',
    title: 'Weather forecast',
    description:
      'Open the weather outlook for your map location — daily and hourly forecasts, past climate patterns, and optional email alerts.',
    action: 'none',
    position: 'bottom-left',
    pointer: 'left',
  },
  {
    id: 'settings',
    title: 'Settings',
    description: 'Configure units, language, and other app preferences.',
    action: 'none',
    position: 'bottom-left',
    pointer: 'left',
  },
  {
    id: 'directions',
    title: 'Directions',
    description: 'Get turn-by-turn directions between any two locations.',
    action: 'none',
    position: 'bottom-left',
    pointer: 'left',
  },
  {
    id: 'done',
    title: "You're all set!",
    description:
      "Explore the map, search places, and contribute to Africa's most detailed address system. Enjoy!",
    action: 'collapse-left-menu',
    position: 'center',
    pointer: 'none',
  },
];

/** Onboarding when `?campaign_id=` — does not use general map FAB / left-menu steps. */
export const CAMPAIGN_TOUR_STEPS: TourStep[] = [
  {
    id: 'campaign-welcome',
    title: 'Field campaign mode',
    description:
      'You are in a live campaign workspace. The campaign tab holds zones, sessions, overlays, and hardware — separate from the main map explore tour.',
    action: 'none',
    position: 'center',
    pointer: 'none',
  },
  {
    id: 'campaign-dock-pill',
    title: 'Campaign tab',
    description:
      'Tap this pill to expand your tools: details, large start/stop, and a grid of quick actions (zoom, locate, profile, settings). Tap again to collapse when you need a clear map.',
    action: 'expand-campaign-dock',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'campaign-details-card',
    title: 'Details card',
    description:
      'Overlays, selected zone, device requirements, live capture stream, and team activity live here. Pick a zone on the map (or open a console link) before starting a session.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'campaign-session-actions',
    title: 'Start & end session',
    description:
      'Start needs your campaign to be active, a selected zone, and sign-in, plus phone location or assigned field hardware when your org requires it. End finishes your active session.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'campaign-tool-grid',
    title: 'Quick tools grid',
    description:
      'Nine slots (3×3 with two in the last row): zoom, my location, account, settings, docs, refresh, and more — same interaction model as stacked map controls.',
    action: 'none',
    position: 'bottom-right',
    pointer: 'right',
  },
  {
    id: 'campaign-done',
    title: 'Ready to collect',
    description:
      'Replay the map tour from Settings if you open Coordal without a campaign. Good luck in the field!',
    action: 'collapse-campaign-dock',
    position: 'center',
    pointer: 'none',
  },
];

const PUBLIC_TOUR_KEY = 'coordal-tour-completed';
const CAMPAIGN_TOUR_KEY = 'coordal-campaign-tour-completed';

export type TourKind = 'public' | 'campaign';

interface TourState {
  isActive: boolean;
  tourKind: TourKind | null;
  steps: TourStep[];
  currentStepIndex: number;
  currentStep: TourStep | null;
  /** Public/explore map tour (legacy field name `hasCompleted` in consumers = this). */
  hasCompleted: boolean;
  hasCompletedCampaign: boolean;
  startTour: () => void;
  startCampaignTour: () => void;
  nextStep: () => void;
  /** Advance past steps whose tour targets are not in the DOM. */
  advanceStep: () => void;
  skipTour: () => void;
  reset: () => void;
  restartTour: () => void;
  restartCampaignTour: () => void;
}

function markTourComplete(kind: TourKind | null): Partial<TourState> {
  if (kind === 'campaign') {
    localStorage.setItem(CAMPAIGN_TOUR_KEY, 'true');
    return {
      isActive: false,
      tourKind: null,
      steps: [],
      currentStepIndex: 0,
      currentStep: null,
      hasCompletedCampaign: true,
    };
  }
  localStorage.setItem(PUBLIC_TOUR_KEY, 'true');
  return {
    isActive: false,
    tourKind: null,
    steps: [],
    currentStepIndex: 0,
    currentStep: null,
    hasCompleted: true,
  };
}

export const useTourStore = create<TourState>((set, get) => ({
  isActive: false,
  tourKind: null,
  steps: [],
  currentStepIndex: 0,
  currentStep: null,
  hasCompleted: localStorage.getItem(PUBLIC_TOUR_KEY) === 'true',
  hasCompletedCampaign: localStorage.getItem(CAMPAIGN_TOUR_KEY) === 'true',

  startTour: () => {
    if (get().hasCompleted) return;
    set({
      isActive: true,
      tourKind: 'public',
      steps: TOUR_STEPS,
      currentStepIndex: 0,
      currentStep: TOUR_STEPS[0],
    });
  },

  startCampaignTour: () => {
    if (get().hasCompletedCampaign) return;
    set({
      isActive: true,
      tourKind: 'campaign',
      steps: CAMPAIGN_TOUR_STEPS,
      currentStepIndex: 0,
      currentStep: CAMPAIGN_TOUR_STEPS[0],
    });
  },

  nextStep: () => {
    const { currentStepIndex, steps, tourKind } = get();
    const nextIndex = currentStepIndex + 1;

    if (nextIndex >= steps.length) {
      set(markTourComplete(tourKind));
      return;
    }

    set({
      currentStepIndex: nextIndex,
      currentStep: steps[nextIndex]!,
    });
  },

  advanceStep: () => {
    const { currentStepIndex, steps, tourKind } = get();
    let nextIndex = currentStepIndex + 1;

    while (nextIndex < steps.length) {
      const step = steps[nextIndex]!;
      if (step.skipIfMissingTarget && typeof document !== 'undefined') {
        const el = document.querySelector(`[data-tour-target="${step.id}"]`);
        if (!el) {
          nextIndex += 1;
          continue;
        }
      }
      break;
    }

    if (nextIndex >= steps.length) {
      set(markTourComplete(tourKind));
      return;
    }

    set({
      currentStepIndex: nextIndex,
      currentStep: steps[nextIndex]!,
    });
  },

  skipTour: () => {
    const { tourKind } = get();
    set(markTourComplete(tourKind));
  },

  reset: () => {
    localStorage.removeItem(PUBLIC_TOUR_KEY);
    localStorage.removeItem(CAMPAIGN_TOUR_KEY);
    set({
      isActive: false,
      tourKind: null,
      steps: [],
      currentStepIndex: 0,
      currentStep: null,
      hasCompleted: false,
      hasCompletedCampaign: false,
    });
  },

  restartTour: () => {
    localStorage.removeItem(PUBLIC_TOUR_KEY);
    set({
      isActive: true,
      tourKind: 'public',
      steps: TOUR_STEPS,
      currentStepIndex: 0,
      currentStep: TOUR_STEPS[0],
      hasCompleted: false,
    });
  },

  restartCampaignTour: () => {
    localStorage.removeItem(CAMPAIGN_TOUR_KEY);
    set({
      isActive: true,
      tourKind: 'campaign',
      steps: CAMPAIGN_TOUR_STEPS,
      currentStepIndex: 0,
      currentStep: CAMPAIGN_TOUR_STEPS[0],
      hasCompletedCampaign: false,
    });
  },
}));
